import { copyDate, copyDates, dateToString } from '../../../utilities/calendar-utilities';
import CalendarMonth from './CalendarMonth';
import CalendarYear from './CalendarYear';
import CalendarRangeDay from './CalendarRangeDay';
import CoreComponent from 'utilities/component-utilities/CoreComponent';

// #region model definitions
interface CalendarRangeProps {    
    name: string,
    className?: string,
    showWeekNumber?: boolean,
    minDate?: Date,
    maxDate?: Date,
    disabledDates?: Array<Date>,
    selectedDates?: Date[],
    onDatesSelected?: (date: Date[], e: React.MouseEvent) => void
    returnDates?: 'all' | 'edges',
    calculateAsNights?: boolean,
    view?: CalendarView
}


export type CalendarView = 'date' | 'month' | 'year';
interface IChangeCalendarView {
    month?: number,
    year?: number,
    view?: CalendarView
}
export type ChangeCalendarView = IChangeCalendarView | CalendarView;

interface CalendarRangeState {
    viewMonth: number
    viewYear: number,
    selectedDates: Date[],
    view: CalendarView,
    minDate: Date
}
// #endregion model definitions
class CalendarRange extends CoreComponent<CalendarRangeProps, CalendarRangeState> {
    readonly today: Date;
    private selectedDatesSorted: Date[] = [];
    constructor(props: CalendarRangeProps) {
        super(props);
        this.today = new Date();

        if(this.props.selectedDates?.length) {
            this.selectedDatesSorted = this.lodash.orderBy(this.props.selectedDates);
        }

        this.state = {
                viewMonth: this.today.getMonth(),
                viewYear: this.today.getFullYear(),
                selectedDates: [],
                view: (props.view ? (props.view + "") : 'date') as any,
                minDate: new Date(1970, 1, 1)
        };
    }

    // #region init
    
    componentDidMount() {
        if(this.props.selectedDates?.length) {
            this.selectedDatesSorted = this.lodash.orderBy(this.props.selectedDates);
        }

        if (this.selectedDatesSorted?.length) {
            this.setState({
                viewMonth: this.selectedDatesSorted[0].getMonth(),
                viewYear: this.selectedDatesSorted[0].getFullYear(),
                selectedDates: [...this.selectedDatesSorted],
                minDate: new Date(1970, 1, 1)
            });
        } else if(this.props.minDate) {
            this.setState({
                viewMonth: this.props.minDate.getMonth(),
                viewYear: this.props.minDate.getFullYear(),
            });
        }
    }

    componentDidUpdate() {
        if(this.props.selectedDates?.length) {
            this.selectedDatesSorted = this.lodash.orderBy(this.props.selectedDates);
        }
    }

    // #endregion init

    // #region logic

    // #region onchange
    changeView = (view: ChangeCalendarView, e?: React.MouseEvent | WheelEvent) => {
        if(e) {
            e.stopPropagation();
            e.preventDefault();
        }
        if (typeof view === 'string') {
            this.setState({
                view: view
            });
        }
        else {
            this.setState({
                view: view.view || this.state.view,
                viewMonth: view.month || view.month === 0 ? view.month : this.state.viewMonth,
                viewYear: view.year || this.state.viewYear
            });
        }
    }


    private onDayClick = (dates: Date[], e: React.MouseEvent) => {
        const selectedDays = dates.filter(date => !this.isDisabledDate(date));

        if(!selectedDays.length) return;

        this.setState({
            selectedDates: copyDates(selectedDays),
            viewMonth: selectedDays[0].getMonth(),
            viewYear: selectedDays[0].getFullYear()
        });

        if (this.props.onDatesSelected) {
            this.props.onDatesSelected(selectedDays, e);
        }
    }
    // #endregion onchange
    
    private getMinDate = (): Date | undefined => {
        let minDate = this.props.minDate ? copyDate(this.props.minDate) : undefined;
        if (this.state.minDate) {
            if (!minDate || this.state.minDate > minDate) minDate = this.state.minDate;
        }

        return minDate;
    };

    private isDisabledDate = (date: Date): boolean => {
        if (this.props.minDate && date < this.props.minDate) return true;
        if (this.props.maxDate && date > this.props.maxDate) return true;
        if (this.state.minDate && date < this.state.minDate) return true;
        if (this.props.disabledDates?.some(d => dateToString(d) === dateToString(date))) return true;

        return false;
    }

    private isDisabledYear = (year: number) => {
        const minDate = this.getMinDate();
        if (!minDate) return false;
        if (minDate.getFullYear() > year) return true;
        if (this.props.maxDate) {
            if (this.props.maxDate.getFullYear() < year) return true;
        }
        return false;
    };

    private isDisabledMonth = (month: number, year: number) => {
        const minDate = this.getMinDate();
        if (!minDate) return false;
        if (this.isDisabledYear(year)) return true;
        if (minDate.getMonth() > month && minDate.getFullYear() === year) return true;
        if (this.props.maxDate) {
            if (this.props.maxDate.getMonth() < month && this.props.maxDate.getFullYear() === year) return true;
        }
        return false;
    };
    // #endregion logic

    // #region render

    private renderBody = () => {
        switch (this.state.view) {
            case 'month':
                return <CalendarMonth
                    name={`${this.props.className}-calendar-range`}
                    onChange={this.changeView}
                    value={this.state.viewMonth}
                    viewYear={this.state.viewYear}
                    isDisabled={this.isDisabledMonth}
                />;
            case 'year':
                return <CalendarYear
                    name={`${this.props.className}-calendar-range`}
                    onChange={this.changeView}
                    value={this.state.viewYear}
                    minDate={this.getMinDate()}
                    maxDate={this.props.maxDate}
                    isDisabled={this.isDisabledYear}
                />;
            case 'date':
            default:
                return <CalendarRangeDay
                    name={`${this.props.className}-calendar-range-day`}
                    onChange={(v, e) => this.onDayClick(v, e)}
                    value={this.state.selectedDates}
                    viewYear={this.state.viewYear}
                    viewMonth={this.state.viewMonth}
                    isDisabledDate={this.isDisabledDate}
                    showWeekNumber={this.props.showWeekNumber}
                    changeView={this.changeView}
                    returnDates={this.props.returnDates}
                    calculateAsNights={this.props.calculateAsNights}
                />;
        }
    }

    render() {
        return (
            <div className={`app-calendar ${this.props.className}`}>
                <div className='app-calendar-wrapper shadow shadow-black dark:shadow-neutral-900'>
                    {this.renderBody()}
                </div>
            </div>
        );
    }
    // #endregion render
}
export default CalendarRange;
